import { Component, Mixins, Prop } from 'vue-property-decorator'
import forEach from 'lodash/forEach'
import {
  Chart as ChartJS,
  ChartData,
  ChartDataset,
  ChartOptions,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend, TooltipItem
} from 'chart.js'
import { Line as LineChart } from 'vue-chartjs'
import { mapState } from 'vuex'
import { DateMixin, NumberMixin } from '@kwixeo/interface-vue'
import mapValues from 'lodash/mapValues'
import ColorsMixin from '../../mixins/ColorsMixin'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

@Component({
  components: {
    LineChart
  },
  computed: {
    ...mapState('dashboard', ['widgets'])
  }
})
export default class WidgetChronological extends Mixins(NumberMixin, ColorsMixin, DateMixin) {
  @Prop({ required: true }) value!: KwixeoDashboard.WidgetChronological
  @Prop({ required: true }) datas!: KwixeoDashboard.DashboardDatas
  @Prop({ required: true }) compare!: boolean
  @Prop({ required: true }) initialized!: boolean

  get computedCompare (): boolean {
    if (this.value.params.period !== 'default') {
      return false
    }
    return this.compare
  }

  get dataKey (): (key: string) => string {
    return key => {
      const params = this.value.params

      if (params.filters) {
        mapValues(params.filters, ((filter, index) => {
          key += '-' + index + '-' + filter.join('-')
        }))
      }

      if (params.period) {
        key += '-' + params.period
        if (params.period !== 'default') {
          key += '-' + params.period_value + '-' + params.period_type
        }
      }

      return key
    }
  }

  get availableDatas (): KwixeoDashboard.ChronologicalDatasSources {
    return this.value.params.datas.filter(data => {
      return this.datas[this.dataKey(data.key)] !== null
    })
  }

  get labels (): string[] {
    const datas = this.availableDatas
    if (!datas[0]) {
      return []
    }
    return (this.datas[this.dataKey(datas[0].key)] as KwixeoDashboard.ChronologicalDatas).labels.map(label => label.label)
  }

  get chartDatas (): ChartDataset[] {
    const datas: ChartDataset[] = []
    this.availableDatas.forEach(value => {
      const data: number[] = []
      let cumulativeData = 0
      forEach((this.datas[ this.dataKey(value.key) ] as KwixeoDashboard.ChronologicalDatas).datas, (datas: KwixeoDashboard.WidgetDatas) => {
        cumulativeData += datas[ value.value ]
        data.push(value.type === 'cumulative' ? cumulativeData : datas[ value.value ])
      })

      datas.push({
        label: value.name,
        data,
        type: value.type === 'bar' ? 'bar' : 'line',
        backgroundColor: value.color,
        borderColor: value.color
      })

      if (this.computedCompare) {
        const data: number[] = []
        let cumulativeData = 0
        forEach((this.datas[ 'compare-' + this.dataKey(value.key) ] as KwixeoDashboard.ChronologicalDatas).datas, (datas: KwixeoDashboard.WidgetDatas) => {
          cumulativeData += datas[ value.value ]
          data.push(value.type === 'cumulative' ? cumulativeData : datas[ value.value ])
        })

        datas.push({
          label: value.name + ' N-1',
          data,
          type: value.type === 'bar' ? 'bar' : 'line',
          backgroundColor: this.lightenColor(value.color),
          borderColor: this.lightenColor(value.color)
        })
      }
    })

    return datas.sort((a, b) => {
      const aType = a.type as string
      const bType = b.type as string
      if (aType > bType) return -1
      else if (aType < bType) return 1
      else return 0
    })
  }

  get chartOptions (): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index'
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            count: 5,
            callback: function(value) {
              return Math.floor(Number(value) / 1000) + 'K'
            }
          }
        },
        /*
        x: {
          ticks: {
            maxRotation: 0,
            autoSkip: false
          }
        }
        */
      },
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          intersect: false,
          yAlign: 'bottom',
          caretSize: 0,
          boxPadding: 5,
          callbacks: {
            label: (tooltipItem: TooltipItem<'line'>): string | string[] | void => {
              const value = tooltipItem.raw as number
              let label: string|string[] = tooltipItem.dataset.label + ' : '
              if (Number.isInteger(value)) {
                label += this.showNumber(value)
              } else {
                label += this.formattedNumber(value)
              }

              if (this.computedCompare) {
                if (tooltipItem.datasetIndex % 2 === 0) {
                  const compareValue = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex + 1].data[tooltipItem.dataIndex] as number
                  //const percentage = compareValue === 0 ? null : Number((value*100 / compareValue).toFixed(2))
                  const percentage = compareValue === 0 ? null : Number((((value - compareValue) / compareValue) * 100).toFixed(2))

                  // previous === 0 ? 0 : this.roundNumber(((actual - previous) / previous) * 100, 2)
                  if (percentage) {
                    label += ' (' + (percentage < 0 ? '' : '+') + percentage + '%)'
                  }
                } else {
                  label = [label]
                  if (tooltipItem.datasetIndex + 1 !== tooltipItem.chart.data.datasets.length) {
                    label.push('')
                  }
                }
              }

              return label
            }
          }
        }
      }
    }
  }

  get chartData (): ChartData {
    return {
      labels: this.labels.map(label => {
        const test = label.split(' ')
        if (test.length === 3) {
          return [test[0], test[1] + ' ' + test[2]]
        }
        return label
      }),
      datasets: this.chartDatas
    }
  }
}
